import { useEffect, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, Sparkles as SparkIcon, Heart, ShieldCheck, Clock } from "lucide-react";

import { BUSINESS, services, cities } from "@/lib/site";
import { IMAGES, img } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, faqSchema } from "@/lib/schema";
import { Reveal } from "@/components/site/Reveal";
import { Sparkles, Sparkle } from "@/components/site/Sparkles";
import { WaveDivider } from "@/components/site/WaveDivider";
import { SectionHeading } from "@/components/site/SectionHeading";
import { ServiceStack, CityCard, CityChips } from "@/components/site/CTA";
import { QuoteButton, LinkButton } from "@/components/site/Buttons";
import { Marquee } from "@/components/site/Marquee";
import { HowItWorks } from "@/components/site/HowItWorks";
import { HeroImageGroup } from "@/components/site/HeroImageGroup";
import { LazyQuoteForm } from "@/components/site/LazyQuoteForm";
import { useGoToQuote } from "@/lib/quoteNav";

const HOME_FAQ = [
  {
    question: "What areas does Pretty in Pink Cleaning Service serve?",
    answer:
      "We serve Wetumpka, Alabama and the surrounding communities — Millbrook, Prattville, Montgomery, Tallassee, and Eclectic. If you're nearby, just reach out and we'll confirm we can help.",
  },
  {
    question: "Do you offer both residential and commercial cleaning?",
    answer:
      "Yes. We clean private homes and light commercial spaces — offices, clinics, salons, and small businesses — with recurring or one-time visits tailored to your space.",
  },
  {
    question: "How do I request a quote?",
    answer:
      "Fill out the quote form on our Contact page or call us at (334) 202-3594. Tell us the size and type of space and what you'd like cleaned, and we'll put together a quote.",
  },
  {
    question: "What's included in a standard house cleaning?",
    answer:
      "A standard visit covers dusting, wiping surfaces, sanitizing kitchens and bathrooms, vacuuming, and mopping floors. Deep cleaning and move-in/move-out cleans go further for a top-to-bottom reset.",
  },
  {
    question: "Do I need to be home during the cleaning?",
    answer:
      "No, most clients aren't. We can clean while you're out and lock up when we're done — just let us know your access preference when you book.",
  },
];

const REASONS = [
  {
    icon: Heart,
    title: "Attention to detail",
    body: "We sweat the small things — baseboards, light switches, the spots others skip — so your space truly feels clean.",
  },
  {
    icon: ShieldCheck,
    title: "Reliable every visit",
    body: "Consistent results and a team that shows up when we say we will. We respect your time and your space.",
  },
  {
    icon: Clock,
    title: "Homes & offices",
    body: "From recurring house cleaning to commercial spaces, we tailor each visit to how you use your property.",
  },
];

const GALLERY_PREVIEW = [
  {
    src: IMAGES.kitchenClean2,
    alt: "White stove and blue tile backsplash after a kitchen clean by Pretty in Pink Cleaning Service in Wetumpka, AL",
  },
  {
    src: IMAGES.bathroom1,
    alt: "Double vanity with dark cabinets in a bathroom cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL",
  },
  {
    src: IMAGES.livingRoom1,
    alt: "Living room with bay windows and plank floors vacuumed by Pretty in Pink Cleaning Service in Wetumpka, AL",
  },
  {
    src: IMAGES.floors1,
    alt: "Glossy wood floors in an empty room mopped by Pretty in Pink Cleaning Service in Wetumpka, AL",
  },
  {
    src: IMAGES.bathroom2,
    alt: "Bathtub with gray tile surround scrubbed by Pretty in Pink Cleaning Service near Montgomery, AL",
  },
  {
    src: IMAGES.fireplace1,
    alt: "Brick fireplace and plank floors in a living room cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL",
  },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Pretty in Pink Cleaning Service | Wetumpka, AL Cleaning" },
      {
        name: "description",
        content:
          "Residential & commercial cleaning in Wetumpka, AL and surrounding areas. House cleaning, deep cleaning, move-out & office cleaning. Request a quote today.",
      },
      {
        property: "og:title",
        content: "Pretty in Pink Cleaning Service | Wetumpka, AL",
      },
      {
        property: "og:description",
        content:
          "Residential & commercial cleaning in Wetumpka, AL and surrounding areas. A pretty clean, every time.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: IMAGES.coverBanner },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMAGES.coverBanner },
    ],
    links: [
      {
        rel: "preload",
        as: "image",
        imagesrcset: `${img(IMAGES.hero, 400, 70)} 400w, ${img(IMAGES.hero, 560, 70)} 560w, ${img(IMAGES.hero, 680, 70)} 680w, ${img(IMAGES.hero, 800, 70)} 800w, ${img(IMAGES.hero, 1200, 70)} 1200w`,
        imagesizes: "(min-width: 1024px) 530px, calc(100vw - 32px)",
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd data={faqSchema(HOME_FAQ)} />

      <Hero />
      <Marquee />
      <Intro />
      <ServicesSection />
      <WhySection />
      <GalleryPreview />
      <HowItWorks />
      <AreasSection />
      <HomeFaq />
      <FinalCta />
    </>
  );
}

export default Index;

/* ---------- Hero ---------- */
function Hero() {
  const goQuote = useGoToQuote();
  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-secondary/50 pt-24 md:pt-28">
      {/* drifting blobs */}
      <div
        className="pointer-events-none absolute -right-32 -top-24 h-[34rem] w-[34rem] rounded-full bg-primary/15 blur-3xl"
        data-blob
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-40 top-1/2 h-[28rem] w-[28rem] rounded-full bg-pink-soft/20 blur-3xl"
        data-blob
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-gold/15 blur-3xl"
        data-blob
        aria-hidden
      />
      <Sparkles count={12} className="h-full w-full" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 pb-20 md:px-6 lg:grid-cols-2 lg:pb-28">
        <div className="text-center lg:text-left">
          <p className="font-script text-3xl text-primary md:text-4xl">{BUSINESS.tagline}</p>
          <Reveal
            as="h1"
            variant="words"
            className="h-hero mt-1 font-extrabold text-ink text-balance"
          >
            Residential & Commercial Cleaning in Wetumpka, AL
          </Reveal>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground text-pretty md:text-xl lg:mx-0">
            {BUSINESS.secondary} From weekly home upkeep to spotless offices, we bring dependable,
            detail-driven cleaning to your door across Wetumpka and surrounding areas.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
            <QuoteButton onClick={goQuote}>Get a Quote</QuoteButton>
            <LinkButton href={BUSINESS.phoneHref} variant="outline">
              <Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}
            </LinkButton>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Residential &amp; Commercial Cleaning · Serving Wetumpka &amp; surrounding areas
          </p>
        </div>

        <Reveal variant="scale" delay={100}>
          <HeroImageGroup
            primary={IMAGES.hero}
            primaryAlt="Kitchen with granite counters and mosaic backsplash cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL"
            secondary={IMAGES.bathroom3}
            secondaryAlt="White vanity and toilet with gray tile in a bathroom cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL"
          />
        </Reveal>
      </div>

      {/* scroll hint */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground lg:flex">
        <span className="text-xs uppercase tracking-widest">Scroll</span>
        <span className="scroll-hint h-10 w-px origin-top bg-current" />
      </div>
      <WaveDivider className="relative z-[1]" />
    </section>
  );
}

/* ---------- Intro ---------- */
function Intro() {
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      el.querySelectorAll<HTMLElement>("[data-gold-rule]").forEach((r) => {
        r.style.transform = "scaleX(1)";
      });
      return;
    }
    const rules = Array.from(el.querySelectorAll<HTMLElement>("[data-gold-rule]"));
    rules.forEach((r) => {
      r.style.transform = "scaleX(0)";
      r.style.transformOrigin = "center";
      r.style.transition = "transform 0.8s cubic-bezier(.2,.7,.2,1)";
    });
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            rules.forEach((r, i) => {
              r.style.transitionDelay = `${i * 0.12}s`;
              r.style.transform = "scaleX(1)";
            });
            io.disconnect();
          }
        }
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={rootRef} className="px-4 py-16 md:px-6 md:py-24">
      <div className="mx-auto max-w-3xl text-center">
        <div
          data-gold-rule
          className="mx-auto mb-6 h-px w-24 bg-gold/60"
          style={{ transform: "scaleX(1)" }}
        />
        <Reveal
          as="p"
          variant="words"
          className="h-section font-display font-bold text-ink text-balance"
        >
          A locally owned cleaning company you can trust with your home and your business.
        </Reveal>
        <div
          data-gold-rule
          className="mx-auto mt-6 h-px w-24 bg-gold/60"
          style={{ transform: "scaleX(1)" }}
        />
      </div>
    </section>
  );
}

/* ---------- Services ---------- */
function ServicesSection() {
  return (
    <section className="px-4 pb-24 pt-14 md:px-6 md:pb-20 md:pt-20">
      <Reveal>
        <SectionHeading
          eyebrow="Our Services"
          title="Cleaning services for homes and businesses"
          intro="Pick a service to see what's included, who it's for, and how it works."
        />
      </Reveal>
      <div className="mx-auto mt-12 max-w-5xl">
        <ServiceStack
          items={services.map((s) => ({
            slug: s.slug,
            name: s.name,
            short: s.short,
            blurb: s.blurb,
          }))}
        />
      </div>
      <div className="mt-16 text-center">
        <LinkButton to="/services">Browse all services</LinkButton>
      </div>
    </section>
  );
}

/* ---------- Why (dark) ---------- */
function WhySection() {
  return (
    <section className="surface-deep grain relative">
      <WaveDivider variant="toDeep" className="absolute -top-[60px] left-0 md:-top-[90px]" />
      {/* faint background word */}
      <span
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-script text-[40vw] leading-none text-white/[0.04] md:text-[26rem]"
        aria-hidden
      >
        Pretty
      </span>
      <div className="relative z-[1] mx-auto max-w-6xl px-4 py-20 md:px-6 md:py-28">
        <Reveal>
          <SectionHeading
            eyebrow="Why Choose Us"
            title="Dependable cleaning, done with care"
            intro="We focus on the details that make a space feel genuinely clean — and show up reliably, visit after visit."
            tone="dark"
          />
        </Reveal>
        <div className="mx-auto mt-14 grid max-w-5xl gap-8 sm:grid-cols-3">
          {REASONS.map((r, i) => (
            <Reveal key={r.title} variant="blur" delay={i * 100} className="text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-pink-soft">
                <r.icon className="h-7 w-7" />
              </span>
              <h3 className="mt-5 text-xl font-bold text-white">{r.title}</h3>
              <p className="mx-auto mt-2 max-w-xs text-sm text-white/70 text-pretty">{r.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
      <WaveDivider variant="toBlush" className="relative z-[1]" />
    </section>
  );
}

/* ---------- Gallery preview (masonry, clip wipe) ---------- */
function GalleryPreview() {
  return (
    <section className="px-4 py-16 md:px-6 md:py-24">
      <Reveal>
        <SectionHeading
          eyebrow="Gallery"
          title="A peek at pretty-clean spaces"
          intro="A few shots of the kind of results we aim for on every visit."
        />
      </Reveal>
      <div className="mx-auto mt-12 grid max-w-6xl grid-cols-2 gap-3 md:grid-cols-3">
        {GALLERY_PREVIEW.map((g, i) => (
          <Reveal key={i} delay={i * 70} className={i % 3 === 1 ? "md:translate-y-6" : ""}>
            <Link
              to="/gallery"
              className="group relative block overflow-hidden rounded-2xl border border-border"
            >
              <img
                src={img(g.src, 800)}
                srcSet={`${img(g.src, 320)} 320w, ${img(g.src, 480)} 480w, ${img(g.src, 640)} 640w, ${img(g.src, 800)} 800w, ${img(g.src, 1200)} 1200w`}
                sizes="(min-width: 1024px) 380px, (min-width: 768px) 33vw, 50vw"
                alt={g.alt}
                loading="lazy"
                decoding="async"
                width={1200}
                height={1500}
                className="aspect-[4/5] w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-primary/30 opacity-0 backdrop-blur-[1px] transition-opacity duration-300 group-hover:opacity-100">
                <span className="rounded-full bg-white/90 px-4 py-1.5 text-xs font-semibold text-primary">
                  View
                </span>
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 text-center">
        <LinkButton to="/gallery">See the full gallery</LinkButton>
      </div>
    </section>
  );
}

/* ---------- Service areas ---------- */
function AreasSection() {
  return (
    <section className="surface-blush grain relative px-4 py-16 md:px-6 md:py-24">
      <div className="relative z-[1]">
        <Reveal>
          <SectionHeading
            eyebrow="Service Areas"
            title="Cleaning across the River Region"
            intro="We're based in Wetumpka and proudly serve these nearby communities."
          />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cities.map((c, i) => (
            <Reveal key={c.slug} variant="up" delay={i * 70}>
              <CityCard slug={c.slug} name={c.name} state={c.state} blurb={c.blurb} />
            </Reveal>
          ))}
        </div>
        <CityChips />
        <div className="mt-10 text-center">
          <LinkButton to="/service-areas" variant="outline">
            View all service areas
          </LinkButton>
        </div>
      </div>
    </section>
  );
}

/* ---------- Home FAQ ---------- */
function HomeFaq() {
  return (
    <section className="px-4 py-16 md:px-6 md:py-24">
      <Reveal>
        <SectionHeading
          eyebrow="FAQ"
          title="Quick answers to common questions"
          intro="Everything you'd want to know before we show up with the microfiber cloths."
        />
      </Reveal>
      <div className="mx-auto mt-10 max-w-3xl divide-y divide-border rounded-2xl border border-border bg-card">
        {HOME_FAQ.map((q) => (
          <details key={q.question} className="group p-5 md:p-6">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-ink">
              {q.question}
              <span className="text-primary transition-transform duration-300 group-open:rotate-45 group-open:text-primary">
                +
              </span>
            </summary>
            <p className="mt-3 text-sm text-muted-foreground md:text-base text-pretty">
              {q.answer}
            </p>
          </details>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Link to="/faq" className="font-semibold text-primary underline-offset-4 hover:underline">
          See all FAQs
        </Link>
      </div>
    </section>
  );
}

/* ---------- Final CTA (dark, with the quote form) ---------- */
function FinalCta() {
  const goQuote = useGoToQuote();
  return (
    <section className="surface-deep grain relative px-4 py-16 md:px-6 md:py-24">
      <Sparkles count={10} color="pink" className="h-full w-full" />
      <div className="relative z-[1] mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
        <div className="text-center lg:text-left">
          <Reveal
            as="p"
            variant="words"
            className="font-script text-3xl text-pink-soft md:text-4xl"
          >
            A Pretty Clean. Every Time.
          </Reveal>
          <Reveal
            as="h2"
            variant="words"
            className="h-section mt-2 font-bold text-white text-balance"
          >
            Ready for a pretty clean?
          </Reveal>
          <Reveal delay={120}>
            <p className="mx-auto mt-4 max-w-xl text-base text-white/75 text-pretty lg:mx-0">
              Residential and commercial cleaning across Wetumpka and surrounding areas. Fill out
              the form and we&rsquo;ll be in touch by your preferred method.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              <QuoteButton onClick={goQuote} variant="solid-white">
                Get a Quote
              </QuoteButton>
              <LinkButton href={BUSINESS.phoneHref} variant="outline-white">
                <Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}
              </LinkButton>
            </div>
          </Reveal>
        </div>
        <Reveal variant="scale" delay={120}>
          <LazyQuoteForm page="Home" />
        </Reveal>
      </div>
    </section>
  );
}
